import useMediaQuery from '@mui/material/useMediaQuery';
import { PropsWithChildren } from 'react';
import { useTheme } from '@mui/material';
import {
  ScreenSizeRange,
  ScreenSizeDown,
  ScreenSizeExclude,
  ScreenSizeUp,
  ViewOnScreenVariants,
} from '../../types/screen';

interface ScreenProps extends PropsWithChildren {
  screen?: ViewOnScreenVariants;
}

/**
 * ViewOnScreen Component
 *
 * This component show or hide children components based on the props passed.
 *
 * @interface ScreenProps
 * @property {children} - Pass children components that has to be shown/hidden based on screen size.
 * @property {ScreenSizeUp} up - Displays children components on screen sizes above the breakpoint.
 * @property {ScreenSizeDown} down - Displays children components on screen sizes below the breakpoint.
 * @property {ScreenSizeRange} range - Displays children components on screen sizes between the breakpoint.
 * @property {ScreenSizeExclude} exclude - Displays children components on screen sizes excluding the breakpoint.
 *
 * Only the first value passed from up | down | range | exclude will be used and rest will discarded.
 *
 * @example
 * <ViewOnScreen screen={{ up: 'sm' }} />
 *  Children components
 * </ViewOnScreen>
 */

function ViewOnScreen({ screen, children }: ScreenProps) {
  const theme = useTheme();
  let shouldRender = false;
  let value: ViewOnScreenVariants;

  if (!screen) {
    shouldRender = true;
  } else {
    switch (Object.keys(screen)[0]) {
      case 'up':
        value = screen as ScreenSizeUp;
        shouldRender = useMediaQuery(theme.breakpoints.up(value.up));
        break;
      case 'down':
        value = screen as ScreenSizeDown;
        shouldRender = useMediaQuery(theme.breakpoints.down(value.down));
        break;
      case 'between':
        value = screen as ScreenSizeRange;
        shouldRender = useMediaQuery(theme.breakpoints.between(value.range.start, value.range.end));
        break;
      case 'excluding':
        value = screen as ScreenSizeExclude;
        shouldRender = !useMediaQuery(
          theme.breakpoints.between(value.exclude.start, value.exclude.end),
        );
        break;
      default:
        break;
    }
  }

  return shouldRender ? <>{children}</> : null;
}

export default ViewOnScreen;
